import { useState, useEffect } from 'react';
import { FaSync, FaEllipsisV } from 'react-icons/fa';
import { Menu, MenuItem } from '@mui/material';
import axios from '../../services/api';
import { toast } from 'react-toastify';
import CreateCommunityModal from './modals/CreateCommunityModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import DetailCommunityModal from './modals/DetailCommunityModal';
import BuildingModal from './modals/BuildingModal';
import ContractModal from './modals/ContractModal';
import PublicServiceModal from './modals/PublicServiceModal';
import EntranceModal from './modals/EntranceModal';
import StartCommunityServiceModal from './modals/StartCommunityServiceModal';
import editIcon from '../../styles/edit_icon.png';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export default function CommunityManagement() {
  const { t } = useTranslation();
  const [communities, setCommunities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateBuildingModalOpen, setIsCreateBuildingModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isPublicServiceModalOpen, setIsPublicServiceModalOpen] = useState(false);
  const [isEntranceModalOpen, setIsEntranceModalOpen] = useState(false);
  const [isStartServiceModalOpen, setIsStartServiceModalOpen] = useState(false);
  const [counters, setCounters] = useState({
    totalCommunities: 0,
    totalBuildings: 0,
    activeServices: 0
  });

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get('/communities');
      setCommunities(response.data.communities);
      setCounters(response.data.counters);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchCommunities();
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatus = (startTime, endTime) => {
    if (!startTime || !endTime) return 'inactive';
    return 'active';
  };

  const filteredCommunities = communities.filter(community => {
    const matchesSearch = (
      community.communityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      community.communityAddress.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const status = getStatus(community.startTime, community.endTime);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    return matchesSearch && matchesStatus;
  });
  const handleDelete = async () => {
    try {
      await axios.delete(`/communities/${selectedCommunity._id}`);
      toast.success(t('Community deleted successfully'));
      fetchCommunities();
      setIsConfirmOpen(false);
    } catch (error) {
      console.error(error);
    }
  };
  const handleStartService = () => {
    setIsStartServiceModalOpen(true);
  };
  const handleStopService = async () => {
    try {
      await axios.post(`/communities/stop-service`, { communityId: selectedCommunity._id });
      // toast.success('Service stopped successfully');
      fetchCommunities();
    } catch (error) {
      console.error(error);
    }
  };
  const handleMenuClick = (event, community) => {
    setAnchorEl(event.currentTarget);
    setSelectedCommunity(community);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4 h-counter items-center mb-2">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-black text-gray-500 uppercase tracking-wider">{t('Total Communities')}</div>
          <div className="text-xl font-bold">{counters?.totalCommunities || '-'}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-black text-gray-500 uppercase tracking-wider">{t('Total Buildings')}</div>
          <div className="text-xl font-bold">{counters?.totalBuildings || '-'}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-black text-gray-500 uppercase tracking-wider">{t('In Service')}</div>
          <div className="text-xl font-bold">{counters?.activeServices || '-'}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-black text-gray-500 uppercase tracking-wider">{t('Total Rooms')}</div>
          <div className="text-xl font-bold">{counters?.totalRooms || '-'}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-black text-gray-500 uppercase tracking-wider">{t('Income')}</div>
          <div className="text-xl font-bold">{counters?.totalPayments || '-'}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex justify-between items-center bg-skin-primary p-4 gap-4 mb-6 rounded-xl shadow-lg h-filter">
        <select
          className="bg-white border border-gray-300 rounded-lg px-4 py-2 min-w-[120px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{t('All Status')}</option>
          <option value="active">{t('Active')}</option>
          <option value="inactive">{t('Inactive')}</option>
        </select>

        <div className="flex-1 flex items-center gap-4">
          <input
            type="text"
            placeholder={t('Search communities...')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            onClick={handleRefresh}
            className="p-2 text-gray-600 hover:text-gray-800"
            disabled={isRefreshing}
          >
            <FaSync className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        <button
          onClick={() => { setIsModalOpen(true); setSelectedCommunity(null); }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          {t('+ Create New')}
        </button>
      </div>

      {/* Table */}
      <div className={`${filteredCommunities.length > 0 ? 'overflow-x-auto' : 'overflow-hidden'} h-table-height-counter`}>
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="sticky left-0 z-10 bg-white bg-skin-primary px-6 py-3 text-center text-sm font-semibold text-skin-base min-w-[50px]">
                No.
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">
                {t('Community ID')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[180px]">
                {t('Community Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Entrance')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Building')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Room')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Status')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[120px]">
                {t('Service Time')}
              </th>
              <th className="sticky bg-white right-0 z-10 bg-white px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredCommunities.map((community, index) => {
              const status = getStatus(community.startTime, community.endTime);
              return (
                <tr key={community._id} className="border-t border-skin-base/10">
                  <td className="sticky bg-skin-primary left-0 z-10 bg-skin-primary px-6 py-4 text-sm text-skin-base">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {community.communityId}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {community.communityName}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {community.entranceCount || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {community.buildingCount || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {community.roomCount || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <span className={`px-2 py-1 rounded ${
                      status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    {
                      community.startTime? moment(community.updatedAt).format('YYYY-MM-DD') : '-'
                    }
                    {/* {community.startTime && community.endTime ? (
                      <div className="text-sm">
                        {moment(community.startTime).format('YYYY-MM-DD')} / {moment(community.endTime).format('YYYY-MM-DD')}
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )} */}
                  </td>
                  <td className="sticky bg-skin-primary right-0 z-10 px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedCommunity(community);
                          setIsModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <img src={editIcon} alt="edit" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleMenuClick(e, community)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaEllipsisV />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateCommunityModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCommunity(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedCommunity(null);
          fetchCommunities();
        }}
        community={selectedCommunity}
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t('Delete Community')}
        message={t('Are you sure you want to delete this community? This action cannot be undone.')}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
        type="danger"
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className="mt-2"
        PaperProps={{
          className: 'bg-[#1c1f3b] border-2 border-[#393e591f] rounded-lg shadow-xl app-text',
          elevation: 0
        }}
      >
        <MenuItem className="text-white hover:bg-[#292f4c]"
          onClick={() => {
            setIsEntranceModalOpen(true);
            handleClose();
          }}
        >{t('Entrance')}</MenuItem>
        <MenuItem className="text-white hover:bg-[#292f4c]"
          onClick={() => {
            setIsCreateBuildingModalOpen(true);
            handleClose();
          }}
        >{t('Building')}</MenuItem>

        <MenuItem className="text-white hover:bg-[#292f4c]"
          onClick={() => {
            setIsPublicServiceModalOpen(true);
            handleClose();
          }}
        >{t('Staff')}</MenuItem>

        <MenuItem className="text-white hover:bg-[#292f4c]"
          onClick={() => {
            setIsContractModalOpen(true);
            handleClose();
          }}
        >{t('Contract')}</MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedCommunity.startTime) {
              handleStopService();
            } else {
              handleStartService();
            }
            handleClose();
          }}
          className="text-white hover:bg-[#292f4c]"
        >
          {selectedCommunity?.startTime ? t('Stop Service') : t('Start Service')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsConfirmOpen(true);
            handleClose();
          }}
          className="text-white hover:bg-[#292f4c]"
        >
          {t('Delete')}
        </MenuItem>
      </Menu>

      <DetailCommunityModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        community={selectedCommunity}
      />
      <BuildingModal
        isOpen={isCreateBuildingModalOpen}
        onClose={() => setIsCreateBuildingModalOpen(false)}
        onSuccess={() => {fetchCommunities();}}
        community={selectedCommunity}
      />
      <ContractModal
        isOpen={isContractModalOpen}
        onClose={() => {
          setIsContractModalOpen(false);
          setSelectedCommunity(null);
        }}
        community={selectedCommunity}
        onSuccess={fetchCommunities}
      />
      <PublicServiceModal
        isOpen={isPublicServiceModalOpen}
        onClose={() => {
          setIsPublicServiceModalOpen(false);
          setSelectedCommunity(null);
        }}
        community={selectedCommunity}
        onSuccess={fetchCommunities}
      />
      <EntranceModal
        isOpen={isEntranceModalOpen}
        onClose={() => {
          setIsEntranceModalOpen(false);
          setSelectedCommunity(null);
        }}
        community={selectedCommunity}
        onSuccess={fetchCommunities}
      />
      <StartCommunityServiceModal
        isOpen={isStartServiceModalOpen}
        onClose={() => {
          setIsStartServiceModalOpen(false);
          setSelectedCommunity(null);
        }}
        community={selectedCommunity}
        onSuccess={fetchCommunities}
      />
    </div>
  );
}